import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from 'pdfjs-dist';

// Configuration du worker pour Vite - utilise le worker local avec chemin dynamique
const getWorkerPath = () => {
  const hostname = window.location.hostname;
  // En dev (Vite, jsdom) ou sur le domaine dédié, `/app/*` est proxifié vers 360 —
  // il faut servir le worker depuis la racine pour utiliser le fichier local de `public/`.
  if (
    hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname.includes('recensement-citoyen-obligatoire.')
  ) {
    return '/pdf.worker.min.js';
  }
  return '/app/administres/recensement-citoyen/pdf.worker.min.js';
};

GlobalWorkerOptions.workerSrc = getWorkerPath();

/**
 * Détermine le type de fichier à partir des données base64
 */
export function detectFileType(base64Data: string): 'pdf' | 'image' | 'unknown' {
  try {
    const pure = base64Data.startsWith('data:') ? base64Data.split(',')[1] : base64Data;
    const binStr = atob(pure);
    const bytes = new Uint8Array(Math.min(10, binStr.length));
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = binStr.charCodeAt(i);
    }

    // Vérifier les signatures de fichiers
    const header = String.fromCharCode(...bytes.slice(0, 4));

    // PDF
    if (header === '%PDF') {
      return 'pdf';
    }

    // JPEG
    if (bytes[0] === 0xFF && bytes[1] === 0xD8) {
      return 'image';
    }

    // PNG
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
      return 'image';
    }

    // GIF
    if (header === 'GIF8') {
      return 'image';
    }

    return 'unknown';
  } catch (error) {
    return 'unknown';
  }
}

export function base64ToUint8Array(b64: string): Uint8Array {
  const pure = b64.startsWith('data:') ? b64.split(',')[1] : b64;
  const binStr = atob(pure);
  const bytes = new Uint8Array(binStr.length);
  for (let i = 0; i < binStr.length; i += 1) bytes[i] = binStr.charCodeAt(i);

  // Vérification rapide que c'est un PDF (commence par %PDF)
  const header = String.fromCharCode(...bytes.slice(0, 4));
  if (header !== '%PDF') {
    throw new Error(`Invalid PDF data: expected "%PDF" header, got "${header}"`);
  }

  return bytes;
}

export async function renderPdfPageToDataURL(
  pdfData: Uint8Array,
  pageNumber: number,
  targetWidth: number,
): Promise<string> {
  const pdf: PDFDocumentProxy = await getDocument({ data: pdfData }).promise;

  // Vérifier que le numéro de page est valide
  if (pageNumber < 1 || pageNumber > pdf.numPages) {
    throw new Error(`Invalid page number: ${pageNumber}. PDF has ${pdf.numPages} pages.`);
  }

  const page = await pdf.getPage(pageNumber);

  const viewport0 = page.getViewport({ scale: 1 });
  const scale = targetWidth / viewport0.width;
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);

  await page.render({
    canvasContext: ctx,
    viewport,
    canvas,
  }).promise;

  const url = canvas.toDataURL('image/png');
  canvas.width = 0;
  canvas.height = 0;
  return url;
}

/**
 * Extrait toutes les pages d'un PDF et retourne un tableau d'URLs de vignettes
 * Version qui travaille avec base64 pour éviter les ArrayBuffer détachés
 */
/**
 * Redimensionne une image à partir de données base64
 */
export function resizeImageFromBase64(
  base64Data: string,
  targetWidth: number,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Impossible de créer le contexte canvas'));
        return;
      }

      // Calculer les dimensions en gardant le ratio
      const aspectRatio = img.height / img.width;
      canvas.width = targetWidth;
      canvas.height = targetWidth * aspectRatio;

      // Dessiner l'image redimensionnée
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Convertir en base64
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };

    img.onerror = () => {
      reject(new Error('Erreur lors du chargement de l\'image'));
    };

    // Ajouter le préfixe data: si nécessaire
    const dataUrl = base64Data.startsWith('data:') ? base64Data : `data:image/jpeg;base64,${base64Data}`;
    img.src = dataUrl;
  });
}

/**
 * Extrait toutes les pages d'un fichier (PDF ou image)
 */
export async function extractAllPages(
  base64Data: string,
  targetWidth: number,
): Promise<Array<{ thumbnailUrl: string; pageNumber: number }>> {
  const fileType = detectFileType(base64Data);

  if (fileType === 'image') {
    // Pour les images, on retourne juste l'image redimensionnée
    const resizedImage = await resizeImageFromBase64(base64Data, targetWidth);
    return [{ thumbnailUrl: resizedImage, pageNumber: 1 }];
  }

  if (fileType === 'pdf') {
    // Pour les PDFs, on utilise la fonction existante
    return extractAllPdfPages(base64Data, targetWidth);
  }

  throw new Error(`Type de fichier non supporté: ${fileType}`);
}

export async function extractAllPdfPages(
  base64Data: string,
  targetWidth: number = 200,
): Promise<Array<{ pageNumber: number; thumbnailUrl: string }>> {
  // Première conversion pour obtenir le nombre de pages
  const initialPdfBytes = base64ToUint8Array(base64Data);
  const pdf: PDFDocumentProxy = await getDocument({ data: initialPdfBytes }).promise;
  const results = [];

  // Traitement séquentiel, reconversion à chaque fois
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
    // Reconvertir les données base64 pour chaque page pour éviter les ArrayBuffer détachés
    const freshPdfBytes = base64ToUint8Array(base64Data);
    const thumbnailUrl = await renderPdfPageToDataURL(freshPdfBytes, pageNum, targetWidth);
    results.push({
      pageNumber: pageNum,
      thumbnailUrl,
    });
  }

  return results;
}
